import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/pulso-logo.png';
import { LEGAL, isPlaceholder } from './legalDetails';
import '../../styles/pulso.css';
import '../../styles/legal.css';

/*
   Shared shell for /about, /privacy, /terms and /data-deletion.

   Reuses the contact page's header and footer classes (.hp-*) rather than
   adding a new header type, so the testing strip offset in pulso.css already
   applies. Colours are Pulso's fixed brand tokens, never the tenant --p-*
   ones: these pages speak for Pulso itself, not for whichever company happens
   to be signed in on this browser.
*/

/** A fact from legalDetails.js, highlighted while it is still a placeholder. */
export function Fact({ value }) {
  return isPlaceholder(value) ? <mark className="lg-ph">{value}</mark> : <>{value}</>;
}

/** An email address as a mailto link — or, while unknown, as a placeholder. */
export function Email({ value }) {
  return isPlaceholder(value) ? <Fact value={value} /> : <a href={`mailto:${value}`}>{value}</a>;
}

/**
 * A numbered clause heading. The number hangs in its own column so the clause
 * titles line up, and the id gives every clause a stable #section-N anchor.
 * Leave `n` out for an unnumbered heading such as "Things to know".
 */
export function Clause({ n, id, children }) {
  return (
    <h2 id={id || (n != null ? `section-${n}` : undefined)} className="lg-h">
      {n != null && <span className="lg-num">{n}.</span>}
      <span className="lg-h-text">{children}</span>
    </h2>
  );
}

// Short documents (the deletion steps) read better without a contents list.
const MIN_CONTENTS = 6;

export default function LegalLayout({ title, children }) {
  const docRef = useRef(null);
  const [contents, setContents] = useState([]);

  // Contents are read back from the rendered clauses, so a page only ever
  // writes its headings once.
  useLayoutEffect(() => {
    const headings = docRef.current ? docRef.current.querySelectorAll('h2.lg-h[id]') : [];
    setContents(Array.from(headings, (h) => ({
      id: h.id,
      n: h.querySelector('.lg-num')?.textContent || '',
      label: h.querySelector('.lg-h-text')?.textContent || '',
    })));
  }, [title]);

  const showContents = contents.length >= MIN_CONTENTS;

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · Pulso`;
    // Arriving from a footer link would otherwise keep the previous page's
    // scroll position and open the document halfway down.
    window.scrollTo(0, 0);
    return () => { document.title = previous; };
  }, [title]);

  return (
    <div className="hp-root">

      <header className="hp-header">
        <div className="hp-header-inner">
          <Link to="/" className="nav-logo">
            <img src={logo} alt="Pulso" className="nav-logo-img" style={{ width: 34 }} />
            <div className="nav-logo-text-wrap">
              <span className="nav-logo-name">PULSO</span>
              <span className="nav-logo-by">{LEGAL.companyName}</span>
            </div>
          </Link>
          <Link to="/" className="hp-admin-btn">Back to Pulso</Link>
        </div>
      </header>

      <main className={`lg-main${showContents ? ' lg-main--toc' : ''}`}>
        <header className="lg-head">
          <h1 className="lg-title">{title}</h1>
          <dl className="lg-meta">
            <div><dt>Last updated</dt><dd><Fact value={LEGAL.lastUpdated} /></dd></div>
            <div><dt>Issued by</dt><dd>{LEGAL.companyName}</dd></div>
          </dl>
        </header>

        {showContents && (
          <nav className="lg-toc" aria-labelledby="lg-toc-title">
            <p className="lg-toc-title" id="lg-toc-title">Contents</p>
            <ol>
              {contents.map((c) => (
                <li key={c.id}>
                  <a href={`#${c.id}`}><span className="lg-toc-num">{c.n}</span>{c.label}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <article className="lg-doc" ref={docRef}>{children}</article>
      </main>

      <footer className="hp-footer">
        <nav className="lg-links" aria-label="Legal">
          <Link to="/about">About</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/data-deletion">Data Deletion</Link>
        </nav>
        © {new Date().getFullYear()} {LEGAL.companyName}. Pulso is operated by {LEGAL.companyName}.
      </footer>

    </div>
  );
}
